import FormProps from "data/forms"
import IndustryEngagementFields from "./types"
import SearchSelect from "Components/molecule/inputs/search-select"
import MultiSelect from "Components/molecule/inputs/multi-select"
import { SelectOption } from "Components/molecule/inputs/select-input";
import DateInput from "Components/molecule/inputs/date-input";
import PrimaryButton from "Components/atoms/buttons/primary-button";
import SecondaryButton from "Components/atoms/buttons/secondary-button";
import TextInput from "Components/molecule/inputs/text-input";
import TextAreaInput from "Components/molecule/inputs/textarea-inpuit";
import FormContainer from "Components/atoms/containers/form-container";

interface IndustryEngagementFormProps extends FormProps<IndustryEngagementFields> {
    statuses: SelectOption[]
    employees: SelectOption[]
    opportunities: SelectOption[]
}

const NAME_KEY = "name";
const DATE_KEY = "date";
const NOTES_KEY = "description";
const STATUS_KEY = "statusId";
const ATTENDEES_KEY = "attendeeIds";
const OPPORTUNITIES_KEY = "opportunityIds";

const IndustryEngagementForm = ({
    handleChange,
    handleBlur,
    handleSubmit,
    handleCancel,
    setFieldValue,
    statuses,
    employees,
    opportunities,
    touched,
    values,
    errors,
    isSubmitting,
    isValid,
}: IndustryEngagementFormProps) => {

    // Convert attendeeIds (string[]) to SelectOption[] for MultiSelect
    const selectedAttendees: SelectOption[] = (values.attendeeIds ?? [])
        .map(id => employees.find(e => e.value === id))
        .filter(Boolean) as SelectOption[];

    // Convert opportunityIds (string[]) to SelectOption[] for MultiSelect
    const selectedOpportunities: SelectOption[] = (values.opportunityIds ?? [])
        .map(id => opportunities.find(o => o.value === id))
        .filter(Boolean) as SelectOption[];

    return <FormContainer
        buttonsClassName="d-flex flex-column flex-md-row gap-2"
        inputs={<>
            <TextInput
                className="col-12 col-md-4"
                name={NAME_KEY}
                key={NAME_KEY}
                label={"Name*"}
                placeholder={"Event name"}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.name && !!errors.name}
                message={touched.name ? errors.name : undefined}
            />

            <SearchSelect
                className="col-12 col-md-4"
                name={STATUS_KEY}
                key={STATUS_KEY}
                label={"Status*"}
                placeholder={"Status"}
                value={values.statusId}
                error={!!touched.statusId && !!errors.statusId}
                message={touched.statusId ? errors.statusId : undefined}
                options={statuses}
                onChange={(option: SelectOption | null) => handleChange(STATUS_KEY)(option ? option.value : '')}
                onBlur={handleBlur(STATUS_KEY)}
            />

            <DateInput
                className="col-12 col-md-4"
                name={DATE_KEY}
                key={DATE_KEY}
                label={"Date"}
                placeholder={"Event date"}
                value={values.date || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.date && !!errors.date}
                message={touched.date ? errors.date : undefined}
            />

            <MultiSelect
                name={ATTENDEES_KEY}
                label={"Attendees*"}
                placeholder={"Select attendees"}
                value={selectedAttendees}
                error={!!touched.attendeeIds && !!errors.attendeeIds}
                message={touched.attendeeIds ? errors.attendeeIds as string : undefined}
                options={employees}
                className="col-12"
                onChange={(options: SelectOption[]) => {
                    if (!setFieldValue) {
                        throw new Error("setFieldValue not defined")
                    }
                    setFieldValue(ATTENDEES_KEY, options.map(o => o.value))
                }}
                onBlur={handleBlur(ATTENDEES_KEY)}
            />

            <MultiSelect
                name={OPPORTUNITIES_KEY}
                label={"Opportunities"}
                placeholder={"Select opportunities"}
                value={selectedOpportunities}
                error={!!touched.opportunityIds && !!errors.opportunityIds}
                message={touched.opportunityIds ? errors.opportunityIds as string : undefined}
                options={opportunities}
                className="col-12"
                onChange={(options: SelectOption[]) => {
                    if (!setFieldValue) {
                        throw new Error("setFieldValue not defined")
                    }
                    setFieldValue(OPPORTUNITIES_KEY, options.map(o => o.value))
                }}
            />

            <TextAreaInput
                name={NOTES_KEY}
                key={NOTES_KEY}
                label={"Notes"}
                placeholder={"Notes"}
                value={values.description}
                onBlur={handleBlur}
                onChange={handleChange}
                className="col-12"
                error={!!touched.description && !!errors.description}
                message={touched.description ? errors.description : undefined}
                max={500}
            />
        </>}
        buttons={<>
            <PrimaryButton
                className="col-12 col-md-6 col-lg-2"
                type='submit'
                onClick={() => handleSubmit()}
                disabled={isSubmitting || !isValid}
            >
                {isSubmitting ? "Saving...." : "Save"}
            </PrimaryButton>
            {
                handleCancel &&
                <SecondaryButton
                    className="col-12 col-md-6 col-lg-2"
                    disabled={isSubmitting}
                    onClick={() => handleCancel()}
                >
                    Back
                </SecondaryButton>
            }
        </>}
    />
}

export default IndustryEngagementForm